/**
 * Auth context: holds the current user + token, exposes login/logout.
 * Token + user are persisted to localStorage so a refresh keeps the session.
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { post } from '../api/client';
import { TOKEN_KEY, USER_KEY, LOGOUT_EVENT } from '../config';
import { canSee, sectionsFor } from '../roles';

const AuthContext = createContext(null);

function readStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(readStoredUser);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  // The API client broadcasts this when a request 401s.
  useEffect(() => {
    window.addEventListener(LOGOUT_EVENT, logout);
    return () => window.removeEventListener(LOGOUT_EVENT, logout);
  }, [logout]);

  const login = useCallback(async (email, password) => {
    const data = await post('/api/v1/auth/login', { email, password });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const role = user?.role;

  const value = {
    user,
    token,
    isAuthenticated: Boolean(token),
    isPastor: role === 'pastor',
    isViewer: role === 'viewer',
    role,
    // Which sections this role may see. Nav + routes both read these.
    // ⚠️ UI gating only — see roles.js. The API does not enforce this.
    sections: sectionsFor(role),
    can: (section) => canSee(role, section),
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
